// The i18next singleton, not `src/i18n`, for the same reason as
// `helpers/errorMessage`: this module sits behind a saga helper, and pulling the
// bootstrap in would drag every language file into module graphs that never had it.
import i18next from 'i18next';

/**
 * The refused action, as the server words it, mapped to the translation key
 * that says the same thing in the operator's language.
 *
 * api-users (`supervisorRefusalMessage`) and api-departments
 * (`buildSupervisorRemovalMessage`) refuse an edit that would leave a site
 * supervised by someone who is no longer on it (ADR-082). The refusal carries
 * no machine-readable code, only an English sentence whose wording both
 * services keep identical on purpose, so the sentence is the contract read here.
 */
const KEY_BY_ACTION = {
	'be disabled': 'error.auditorPage.supervisorRefusal.disabled',
	'be deleted': 'error.auditorPage.supervisorRefusal.deleted',
	'be removed from these sites': 'error.auditorPage.supervisorRefusal.removedFromSites',
} as const;

const REMOVED_FROM_DEPARTMENT_KEY = 'error.auditorPage.supervisorRefusal.removedFromDepartment';
const REMOVED_FROM_DEPARTMENT_PREFIX = 'be removed from department ';

/**
 * One refusal sentence. Several are joined with a space when more than one user
 * or department is refused, so the pattern is applied globally.
 *
 * `(.+?)` stays lazy everywhere so a user, department or site name containing
 * the next literal cannot swallow the following sentence. The tail is optional
 * on " from these sites" because api-departments says "Please remove them as
 * supervisor from these sites first." where api-users says "... as supervisor first.".
 */
const REFUSAL_SENTENCE =
	/User (.+?) cannot (be disabled|be deleted|be removed from these sites|be removed from department .+?) because they are a supervisor in the following sites: (.+?)\. Please remove them as supervisor(?: from these sites)? first\./g;

/**
 * Translates one refusal sentence.
 *
 * @returns Localized copy, or `null` when the action is not one we know.
 */
function translateSentence(user: string, action: string, sites: string): string | null {
	const options = { user, sites, interpolation: { escapeValue: false } };

	if (action in KEY_BY_ACTION) {
		return i18next.t(KEY_BY_ACTION[action as keyof typeof KEY_BY_ACTION], options);
	}

	if (action.startsWith(REMOVED_FROM_DEPARTMENT_PREFIX)) {
		const department = action.slice(REMOVED_FROM_DEPARTMENT_PREFIX.length);
		return i18next.t(REMOVED_FROM_DEPARTMENT_KEY, { ...options, department });
	}

	return null;
}

/**
 * Whether a server message is wholly made of supervisor refusal sentences.
 *
 * A caller that has to treat the refusal differently branches on this, not on
 * whether `localizeSupervisorRefusal` changed the text: in English the copy is
 * the server's own sentence, so the localized text equals the input.
 *
 * @param message - The `message` of an api-users / api-departments error body.
 * @returns True when every sentence in it is a refusal this module translates.
 */
export function isSupervisorRefusal(message: string | undefined): message is string {
	if (!message) return false;

	// Everything outside the recognized sentences must be the spaces joining
	// them, or this is a message we only partly understand.
	// `search`, not `test`: the pattern is global, and `test` would leave its
	// `lastIndex` behind for the next call.
	return message.search(REFUSAL_SENTENCE) !== -1 && message.replace(REFUSAL_SENTENCE, '').trim() === '';
}

/**
 * Localizes the server's supervisor refusal (ADR-082) for display.
 *
 * The names in it - user email, site names, department name - are data and are
 * carried over verbatim; only the sentence around them is translated.
 *
 * Anything that is not wholly made of refusal sentences is returned unchanged,
 * so an unrelated error, or a refusal the server has since reworded, still
 * shows the server's own text rather than a half-translated one.
 *
 * @param message - The `message` of an api-users / api-departments error body.
 * @returns The localized refusal, or `message` as it was.
 */
export function localizeSupervisorRefusal(message: string): string;
export function localizeSupervisorRefusal(message: string | undefined): string | undefined;
export function localizeSupervisorRefusal(message: string | undefined): string | undefined {
	if (!isSupervisorRefusal(message)) return message;

	const translated: string[] = [];
	for (const [, user, action, sites] of message.matchAll(REFUSAL_SENTENCE)) {
		const copy = translateSentence(user, action, sites);
		if (copy === null) return message;
		translated.push(copy);
	}

	return translated.length ? translated.join(' ') : message;
}
